// Brand kit assets on the canvas: place one in the top-right corner of the
// current scene, then copy it — same position and size — onto every other
// slide as ONE undo step. A box carries its brand asset in its id
// (`-brand<assetId>-`), so re-applying moves the copy a scene already has
// instead of stacking another one (same idea as Motion Director's `-mdir-`).

import { createBox } from "../../common/common_tools";
import { addBoxThunk, moveBox, resizeBox } from "../../common/actions";
import { isSlide } from "../../common/utils";
import { ID_PREFIX_BOX } from "../../common/constants";
import { brandAssetTag, brandTagOfBox } from "../../common/brand_box_id";

export { brandAssetTag, brandTagOfBox };

/** Unique per scene: Date.now() repeats inside a tight loop, the scene id does not. */
export const brandBoxId = (assetId, sceneId, now = Date.now()) =>
  `${ID_PREFIX_BOX}${now}-${brandAssetTag(assetId)}-${sceneId}`;

export const findBrandBoxInScene = (scene, tag) =>
  ((scene && scene.boxes) || []).find((id) => String(id).includes(`-${tag}-`));

/** Top-right corner for a box `width` % wide, `margin` % from the edges. */
export const topRightPosition = ({ width }, margin = 3) => ({
  x: `${Math.max(0, 100 - Number(width) - margin)}%`,
  y: `${margin}%`,
  type: "absolute",
});

const sizeOf = (structure = {}) => ({
  width: structure.width,
  height: structure.height,
  widthUnit: structure.widthUnit,
  heightUnit: structure.heightUnit,
});

/**
 * Copy the brand box `sourceBoxId` — its current position and size — onto
 * every other slide. The asset comes from the box id, so any copy works.
 * @returns {{ created: number, updated: number, error?: "source-missing" | "not-brand" }}
 */
export function applyBrandAssetToAllScenes(dispatch, getState, { sourceBoxId }) {
  const tag = brandTagOfBox(sourceBoxId);
  if (!tag) return { created: 0, updated: 0, error: "not-brand" };
  const present = getState().undoGroup.present;
  const source = present.boxesById && present.boxesById[sourceBoxId];
  const toolbar = present.pluginToolbarsById && present.pluginToolbarsById[sourceBoxId];
  if (!source || !toolbar) return { created: 0, updated: 0, error: "source-missing" };

  const pluginName = toolbar.pluginId;
  const url = toolbar.state && toolbar.state.url;
  const size = sizeOf(toolbar.structure);
  const position = { ...source.position };
  const navItemsById = present.navItemsById || {};
  const targets = (present.navItemsIds || [])
    .map((id) => navItemsById[id])
    .filter((scene) => scene && scene.id !== source.parent && isSlide(scene.type));

  let created = 0;
  let updated = 0;
  // reducers.jsx groupBy: every dispatch below collapses into one history entry.
  window.cropUndoGroup = `brand-${Date.now()}`;
  try {
    targets.forEach((scene) => {
      const existing = findBrandBoxInScene(scene, tag);
      if (existing) {
        dispatch(moveBox(existing, position.x, position.y, position.type, scene.id, 0));
        dispatch(resizeBox(existing, size));
        updated += 1;
        return;
      }
      const id = `${ID_PREFIX_BOX}${Date.now()}-${tag}-${scene.id}`;
      createBox(
        {
          parent: scene.id,
          container: 0,
          name: pluginName,
          componentType: "Brand",
          position: { ...position },
          id,
          page: scene.id,
          url,
          width: size.width,
          height: size.height,
          widthUnit: size.widthUnit,
          heightUnit: size.heightUnit,
          // Explicit start: visible for the whole scene, not from the playhead.
          timeline: { startFrame: 0, visible: true },
        },
        pluginName,
        true,
        (...args) => dispatch(addBoxThunk(...args)),
        getState().undoGroup.present.boxesById
      );
      // The toolbar reducer ignores the units passed at creation; pin them.
      dispatch(resizeBox(id, size));
      created += 1;
    });
  } finally {
    window.cropUndoGroup = null;
  }
  return { created, updated };
}
